/** Probe only during an observed outage. Normal API traffic is the health signal. */
export function createBackendRecovery(url: string, onUnavailable: () => void, onRecovered: () => void) {
  let active = false;
  let disposed = false;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let controller: AbortController | undefined;
  let delay = 10000;
  let generation = 0;
  let pendingResume = false;
  const canProbe = () => navigator.onLine !== false && document.visibilityState !== 'hidden';
  const clearTimer = () => { clearTimeout(timer); timer = undefined; };
  const recovered = () => {
    if (!active || disposed) return;
    active = false;
    pendingResume = false;
    generation += 1;
    clearTimer();
    controller?.abort();
    delay = 10000;
    onRecovered();
  };
  const schedule = () => {
    if (!active || disposed || timer !== undefined || controller || !canProbe()) return;
    // Positive jitter avoids synchronizing clients after a deployment/outage.
    timer = setTimeout(probe, delay + Math.random() * delay * 0.2);
  };
  async function probe() {
    timer = undefined;
    if (!active || disposed || !canProbe() || controller) return;
    const currentGeneration = generation;
    const request = new AbortController();
    controller = request;
    const timeout = setTimeout(() => request.abort(), 8000);
    try {
      const response = await fetch(url, {
        method: 'POST', credentials: 'include', signal: request.signal,
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ query: 'query BackendHealthCheck { __typename }' }),
      });
      const body = response.ok ? await response.json() : undefined;
      if (currentGeneration === generation && !request.signal.aborted && body?.data?.__typename && !body.errors?.length) recovered();
    } catch {
      // Stay in recovery; do not produce a notification for every failed probe.
    } finally {
      clearTimeout(timeout);
      controller = undefined;
      if (currentGeneration === generation) delay = Math.min(delay * 2, 60000);
      if (pendingResume && active && !disposed && canProbe()) {
        pendingResume = false;
        void probe();
      } else {
        pendingResume = false;
        schedule();
      }
    }
  }
  const resume = () => {
    if (!canProbe()) { pendingResume = false; clearTimer(); controller?.abort(); return; }
    if (active && controller?.signal.aborted) pendingResume = true;
    if (active && !controller) { clearTimer(); void probe(); }
  };
  window.addEventListener('online', resume);
  window.addEventListener('offline', resume);
  document.addEventListener('visibilitychange', resume);
  return {
    failed: () => {
      if (active || disposed) return;
      active = true;
      generation += 1;
      delay = 10000;
      onUnavailable();
      schedule();
    },
    recovered,
    dispose: () => {
      disposed = true;
      active = false;
      pendingResume = false;
      clearTimer();
      controller?.abort();
      window.removeEventListener('online', resume);
      window.removeEventListener('offline', resume);
      document.removeEventListener('visibilitychange', resume);
    },
  };
}
